import { makeStyles, Theme, Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { FC, DragEvent, useRef, useState } from 'react';
import { WbUploaderFileFormat } from './formats';
import { WbUploaderFilePreview } from './WbUploaderFilePreview';
import clsx from 'clsx';

export type WbUploaderProps = {
  value: File[];
  onChange: (files: File[]) => void;
  formats: WbUploaderFileFormat[];
  maxSizeMb: number;
  disabled?: boolean;
  error?: string;
  multiple?: boolean;
  onDownload?: (file: File) => void;
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  dropZone: {
    cursor: 'pointer',
    color: theme.palette.text.primary,
    overflow: 'clip',
    position: 'relative',
    border: `1px dashed ${theme.palette.primary.main}`,
    borderRadius: 4,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    transition: 'background-color 0.2s',
    backgroundColor: 'transparent',
    '&:disabled': {
      opacity: '50%',
    },
  },
  dropZoneActive: {
    backgroundColor: theme.palette.action.hover,
  },
  dropZoneRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  browseText: {
    color: theme.palette.primary.main,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    minHeight: 44,
  },
  helperText: {
    color: theme.palette.text.secondary,
  },
  errorText: {
    color: theme.palette.error.main,
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
}));

export const WbUploader: FC<WbUploaderProps> = ({
  value,
  onChange,
  formats,
  maxSizeMb,
  disabled = false,
  error,
  multiple = true,
  onDownload,
}) => {
  const classes = useStyles();
  const [dragging, setDragging] = useState(false);
  const [validationError, setValidationError] = useState<string>();
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptMimes = formats.map(f => f.mime).join(',');
  const formatLabels = formats.map(f => f.label).join(', ');

  const resolveFormat = (file: File) => formats.find(f => f.mime === file.type);

  const validateAndAdd = (incoming: FileList | File[]) => {
    const maxBytes = maxSizeMb * 1024 * 1024;
    const valid: File[] = [];
    let invalidFormatCount = 0;
    let oversizeCount = 0;

    for (const file of Array.from(incoming)) {
      const allowed = formats.some(fmt => fmt.mime === file.type);
      const sized = file.size <= maxBytes;

      if (!allowed) {
        invalidFormatCount += 1;
        continue;
      }

      if (!sized) {
        oversizeCount += 1;
        continue;
      }

      valid.push(file);
    }

    if (oversizeCount > 0 || invalidFormatCount > 0) {
      const messageParts: string[] = [];

      if (oversizeCount > 0) {
        messageParts.push(
          `${oversizeCount} file${oversizeCount > 1 ? 's' : ''} exceed${
            oversizeCount > 1 ? '' : 's'
          } the maximum size of ${maxSizeMb}MB`,
        );
      }

      if (invalidFormatCount > 0) {
        messageParts.push(
          `${invalidFormatCount} file${
            invalidFormatCount > 1 ? 's have' : ' has'
          } an unsupported format`,
        );
      }

      setValidationError(`${messageParts.join(' and ')}.`);
    } else {
      setValidationError(undefined);
    }

    if (!multiple) {
      onChange(valid.slice(0, 1));
    } else {
      const existing = value.map(f => f.name);
      const deduplicated = valid.filter(f => !existing.includes(f.name));
      onChange([...value, ...deduplicated]);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      validateAndAdd(event.target.files);
    }
    // Reset input so same file can be re-added after deletion
    event.target.value = '';
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!disabled) setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    validateAndAdd(event.dataTransfer.files);
  };

  const handleDelete = (file: File) => {
    onChange(value.filter(f => f !== file));
  };

  const handleBrowseClick = () => {
    if (!disabled) inputRef.current?.click();
  };

  return (
    <div className={classes.root}>
      {value.length > 0 && (
        <div
          className={classes.fileList}
          role="list"
          aria-label="Attached files"
        >
          {value.map(file => (
            <WbUploaderFilePreview
              key={`${file.name}-${file.size}`}
              file={file}
              format={resolveFormat(file)}
              onDownload={onDownload}
              onDelete={handleDelete}
              disabled={disabled}
            />
          ))}
        </div>
      )}
      <button
        type="button"
        disabled={disabled}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        className={clsx(classes.dropZone, dragging && classes.dropZoneActive)}
      >
        <div className={classes.dropZoneRow}>
          <Typography variant="body2">Drag and drop files here or</Typography>
          <span className={classes.browseText}>
            <SearchIcon fontSize="small" />
            <Typography variant="body2" component="span" color="inherit">
              Browse files
            </Typography>
          </span>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={acceptMimes}
          multiple={multiple}
          style={{ display: 'none' }}
          onChange={handleInputChange}
          aria-hidden="true"
          tabIndex={-1}
        />
      </button>
      <Typography
        variant="caption"
        className={
          error || validationError ? classes.errorText : classes.helperText
        }
      >
        {error ||
          validationError ||
          `Supported file: ${formatLabels}. Max file size: ${maxSizeMb}MB`}
      </Typography>
    </div>
  );
};
